import {createFileRoute, notFound} from "@tanstack/react-router";
import {z as zod} from "zod";
import {IST_TIME_ZONE_ID} from "~/utilities/timeZones";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {REPORT__DETAILS__QUERY_KEY} from "~/utilities/queryKeys";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {DateRangeOptions} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import {
    reportZodValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/Report";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {hydrateReportForFrontend, type HydratedReport} from "~/utilities/reports/hydrateReportForFrontend";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/$reportSlug",
)({
    validateSearch: zod.object({
        saved: zod.boolean().optional(),
        "default-date-range": zod.enum(DateRangeOptions).optional(),
    }),
    loader: async ({context, params}) => {
        const reportIdResult = uuidZodValidator.safeParse(params.reportSlug);
        if (reportIdResult.success === false) {
            throw notFound();
        }
        const reportId = reportIdResult.data;

        const report = await context.queryClient.ensureQueryData<HydratedReport | null>({
            queryKey: [REPORT__DETAILS__QUERY_KEY, reportId],
            queryFn: async () => {
                const reportResult = await robustJsonResultClientFetch(
                    `${context.intellsysBaseUrl}/sub-system/pluto/reports/get-by-id`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                            "X-Workspace-Id": context.workspace.id,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            reportId,
                        }),
                    },
                    reportZodValidator.nullable(),
                );
                if (reportResult.success === false) {
                    throw reportResult.error;
                }
                const report = reportResult.data;

                return report === null ? null : hydrateReportForFrontend(report);
            },
            retry: false,
            staleTime: Infinity,
        });
        if (report === null) {
            throw notFound();
        }

        return {
            report,
            workspace: context.workspace,
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            userAccessLevel: context.userAccessLevelOnWorkspace,
            timezone: context.workspace.timeZoneId === IST_TIME_ZONE_ID ? "Asia/Kolkata" : "UTC",
            platformUser: context.profileStatus.platformUser,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
